import inherits from "inherits";

import BaseRenderer from "diagram-js/lib/draw/BaseRenderer";

import {
  append as svgAppend,
  attr as svgAttr,
  classes as svgClasses,
  create as svgCreate
} from "tiny-svg";

import {
  getFillColor,
  getStrokeColor
} from "bpmn-js/lib/draw/BpmnRenderUtil";

import TextUtil from "diagram-js/lib/util/Text";

import * as _ from "lodash";
import { Triggers } from "../ITriggerData";

import BpmnRenderer from "bpmn-js/lib/draw/BpmnRenderer";
import PathMap from "bpmn-js/lib/draw/PathMap";
import EventBus from "diagram-js/lib/core/EventBus";
import { FrendsElementStore } from "../FrendsElementStore";
import colors from "shared/styles/colors";
import { ElementType } from "../IProcessData";
import { USE_DMN_MODE, USE_STATEMENT_MODE } from "../ElementFactory";
import { ThemeType } from "shared/styles/theme";
import { fetchTheme } from "shared/styles/hooks/useUserTheme";

export default function CustomRenderer(eventBus: EventBus, bpmnRenderer: any, pathMap: PathMap, styles: any, frendsStore: FrendsElementStore, config: any) {
  // base priority is 1000 -at 1500 we'll override all renderers with a lower priority
  BaseRenderer.call(this, eventBus, 1500);
  const self = this;
  self.computeStyle = styles.computeStyle;
  self.eventBus = eventBus;
  self.bpmnRenderer = bpmnRenderer;
  self.pathMap = pathMap;

  const defaultFillColor = config.bpmnRenderer && config.bpmnRenderer.defaultFillColor

  const textUtil = new TextUtil({
    style: { fontFamily: "Arial, sans-serif", fontSize: "12px" },
    size: { width: 100 }
  });

  // Declare handlers to override or handle custom elements here:
  self.handlers = {
    "bpmn:ScriptTask": (p: any, shape: BpmnJsElement) => {

      let useStatementMode = false;

      const currentElements = frendsStore.getElements();
      const frendsElement = currentElements && currentElements[shape.id];

      if (frendsElement) {
        useStatementMode =
          frendsElement.parameters[USE_STATEMENT_MODE] &&
          frendsElement.parameters[USE_STATEMENT_MODE].value;
      } else {
        // must be an unsaved element -> see if the shape itself has the flag
        useStatementMode = (shape as any)[USE_STATEMENT_MODE];
      }

      if (useStatementMode) {
        // reset sizes to defaults if the statement mode has been toggled
        shape.width = 100;
        shape.height = 80;
        const defaultRenderer = bpmnRenderer.handlers["bpmn:ScriptTask"];
        return defaultRenderer(p, shape);
      }

      shape.width = 30;
      shape.height = 30;
      const ret = drawRect(p, shape.width, shape.height, 8, 0, {
        strokeWidth: 2,
        stroke: getStrokeColor(shape),
        fill: getFillColor(shape) === "white" ? defaultFillColor : getFillColor(shape)
      });
      renderScriptTaskIcon(p, shape);
      renderExternalLabel(p, shape);
      return ret;
    },
    "bpmn:StartEvent": (parentGfx: any, element: BpmnJsElement) => {
      const startShape = self.bpmnRenderer.drawShape(parentGfx, element);
      if (element.parent != null && element.parent.type === "bpmn:Process") {
        renderEventContent(element, parentGfx);
      }
      return startShape;
    },
    "bpmn:BusinessRuleTask": (parentGfx: any, shape: BpmnJsElement) => {
      const currentElements = frendsStore.getElements();
      const frendsElement = currentElements && currentElements[shape.id];

      const useDmnMode = frendsElement?.type === ElementType.Dmn  // Check the type
        || frendsElement?.parameters[USE_DMN_MODE]?.value         // Fallback to the useDmnMode parameter - this probably never actually happens
        || (shape as any)[USE_DMN_MODE];                          // Must be an unsaved element -> see if the shape itself has the flag

      if (useDmnMode) {
        const ret = drawRect(parentGfx, shape.width, shape.height, 8, 0, {
          strokeWidth: 2,
          stroke: getStrokeColor(shape),
          fill: getFillColor(shape) === "white" ? defaultFillColor : getFillColor(shape)
        });
        renderDmnTaskIcon(parentGfx, shape);
        return ret;
      }

      const defaultRenderer = bpmnRenderer.handlers["bpmn:BusinessRuleTask"];
      return defaultRenderer(parentGfx, shape);
    }
  };

  self.canRender = (element: BpmnJsElement) => {
    const keys = Object.keys(self.handlers);
    const hit = keys.filter((h) => h === element.type);
    // We only want to render elements which we can find in our handler
    return hit.length > 0;
  };

  self.drawShape = (p: any, shape: BpmnJsElement) => {
    return self.handlers[shape.type](p, shape);
  };

  self.drawConnection = (visuals: any, element: BpmnJsElement) => {
    return self.handlers[element.type](visuals, element);
  };

  const renderDmnTaskIcon = (parentGfx: any, element: BpmnJsElement) => {
    const abspos = {
      x: 85,
      y: 70
    };
    const isLightTheme = fetchTheme() === ThemeType.light;
    const strokeColor = `${isLightTheme ? colors.dark4 : colors.grey_3}`;
    const mainFillColor = getFillColor(element) === "white" ? defaultFillColor : getFillColor(element);
    const headerFillColor = `${isLightTheme ? colors.grey_2 : colors.grey_2}`;
    const mainPathData = pathMap.getScaledPath("TASK_TYPE_BUSINESS_RULE_MAIN", { abspos: abspos });
    drawPath(parentGfx, mainPathData, {
      strokeWidth: 1,
      stroke: strokeColor,
      fill: mainFillColor
    });

    const headerPathData = pathMap.getScaledPath("TASK_TYPE_BUSINESS_RULE_HEADER", { abspos: abspos });
    drawPath(parentGfx, headerPathData, {
      strokeWidth: 1,
      stroke: strokeColor,
      fill: headerFillColor
    });
    renderLabel(parentGfx, element.businessObject.name, {
      box: {
        width: element.width,
        height: element.height
      },
      align: "center-middle"
    });
  };

  const renderScriptTaskIcon = (parentGfx: any, element: BpmnJsElement) => {
    const pathData = pathMap.getScaledPath("TASK_TYPE_SCRIPT", {
      containerWidth: element.width,
      containerHeight: element.height,
      position: {
        mx: 1,
        my: 1
      }
    });
    drawPath(parentGfx, pathData, { strokeWidth: 1, stroke: "darkred", fill: "white" });
  };

  const renderEventContent = (element: BpmnJsElement, parentGfx: any) => {
    const currentElements = frendsStore.getElements();
    const frendsElement = currentElements && currentElements[element.id];
    if (!frendsElement) {
      return;
    }
    if (frendsElement.selectedTypeId === Triggers.ManualTrigger.triggerType) {
      return renderLabel(parentGfx, "M", {
        align: "left-top",
        padding: {
          top: element.height !== undefined && element.height / 2 - 6,
          left: element.width !== undefined && element.width / 2 - 5
        },
        style: {
          fontSize: "12px"
        }
      });
    }

    switch (frendsElement.selectedTypeId) {
      case Triggers.HttpTrigger.triggerType:
      case Triggers.HttpApiTrigger.triggerType:
      case Triggers.QueueTrigger.triggerType:
      case Triggers.RabbitMQTrigger.triggerType:
      case Triggers.ServiceBusTrigger.triggerType: {
        const handler = self.bpmnRenderer.handlers["bpmn:MessageEventDefinition"];
        return handler(parentGfx, element);
      }
      case Triggers.ScheduleTrigger.triggerType: {
        const handler = self.bpmnRenderer.handlers["bpmn:TimerEventDefinition"];
        return handler(parentGfx, element);
      }
      case Triggers.FileWatchTrigger.triggerType: {
        const handler = self.bpmnRenderer.handlers["bpmn:ConditionalEventDefinition"];
        return handler(parentGfx, element);
      }
      case Triggers.ConditionalTrigger.triggerType: {
        const handler = self.bpmnRenderer.handlers["bpmn:SignalEventDefinition"];
        return handler(parentGfx, element);
      }
    }
  };

  // The remaining functions are more or less duplicated from the BpmnRenderer:
  // Original from bpmn-js/lib/draw/BpmnRenderer.js line 394
  const renderLabel = (parentGfx: any, label: string | undefined, options: any) => {
    const text = textUtil.createText(label || "", options);
    svgClasses(text).add("djs-label");
    svgAppend(parentGfx, text);
    return text;
  };

  // Original from bpmn-js/lib/draw/BpmnRenderer.js line 406
  const renderExternalLabel = (parentGfx: any, element: BpmnJsElement) => {
    const semantic = element.businessObject;
    const box = {
      width: 90,
      height: 30,
    };
    // CHANGE: Box x and y no set (defaulted to 0), align set to left and padding top element.height+5
    return renderLabel(parentGfx, semantic.name, { box: box, align: "left-top", padding: { top: element.height !== undefined && element.height + 5, }, style: { fontSize: "11px" } });
  };

  // Original from bpmn-js/lib/draw/BpmnRenderer.js line 306
  const drawPath = (parentGfx: any, d: any, attrs: any) => {
    attrs = this.computeStyle(attrs, ["no-fill"], {
      strokeWidth: 2,
      stroke: "black"
    });
    const path = svgCreate("path");
    svgAttr(path, { d: d });
    svgAttr(path, attrs);
    svgAppend(parentGfx, path);
    return path;
  };

  // Original from bpmn-js/lib/draw/BpmnRenderer.js line 233
  const drawRect = (parentGfx: any, width: any, height: any, r: any, offset: any, attrs: any) => {
    offset = offset || 0;

    attrs = self.computeStyle(attrs, {
      stroke: "black",
      strokeWidth: 2,
      fill: defaultFillColor
    });

    const rect = svgCreate("rect");
    svgAttr(rect, {
      x: offset,
      y: offset,
      width: width - offset * 2,
      height: height - offset * 2,
      rx: r,
      ry: r
    });
    svgAttr(rect, attrs);

    svgAppend(parentGfx, rect);

    return rect;
  };
}

inherits(CustomRenderer, BaseRenderer);

CustomRenderer.$inject = ["eventBus", "bpmnRenderer", "pathMap", "styles", "frendsStore", "config"];
